import { AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import { AgGridAngular } from 'ag-grid-angular';
import {
    AllCommunityModule,
    ClientSideRowModelModule,
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ModuleRegistry,
    RowGroupingDisplayType,
} from 'ag-grid-community';
import {
    ClipboardModule,
    ContextMenuModule,
    GroupFilterModule,
    PivotModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    TreeDataModule,
} from 'ag-grid-enterprise';

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnInit,
} from '@angular/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import {
    AG_GRID_LOCALES,
    CURRENCY_VALUE_FORMATTER,
    DATE_CELL_VALUE_FORMATTER,
    DATE_STRING_VALUE_GETTER,
    numberFieldPropertyComparator,
    WdxAgGridCurrencyField,
} from '@wdx/shared/components/ag-grid';
import {
    WdxButtonModule,
    WdxButtonStyle,
} from '@wdx/shared/components/wdx-button';
import { WdxDropdownModule } from '@wdx/shared/components/wdx-dropdown';
import { WdxIconModule } from '@wdx/shared/components/wdx-icon';
import { WdxSpinnerModule } from '@wdx/shared/components/wdx-spinner';
import {
    CashAccount,
    FriendlyProblemDetails,
    InvestmentDataConfiguration,
    InvestmentDataSectionType,
    TranslationsService,
    WdxAgGridThemeDirective,
    WdxDateFormat,
    WdxDateTimeService,
    WdxNumberService,
} from '@wdx/shared/utils';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { WdxSearchInputComponent } from '@wdx/shared/components/wdx-search-input';
import * as R from 'ramda';
import {
    PortfolioLoadingStates,
    PortfolioNotLoadedComponent,
} from '../portfolio-not-loaded/portfolio-not-loaded.component';
import { PortfolioAgGridColumnMapperService } from '../services/portfolio-ag-grid-column-mapper.service';

export interface AccountsData extends CashAccount {
    balance?: WdxAgGridCurrencyField;
}
ModuleRegistry.registerModules([
    AllCommunityModule,
    ClientSideRowModelModule,
    GroupFilterModule,
    PivotModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    TreeDataModule,
    ClipboardModule,
    ContextMenuModule,
]);

@Component({
    selector: 'wdx-portfolio-accounts',
    imports: [
        AgGridAngular,
        WdxAgGridThemeDirective,
        WdxSpinnerModule,
        WdxDropdownModule,
        WdxIconModule,
        NgbDropdownModule,
        WdxButtonModule,
        WdxSearchInputComponent,
        PortfolioNotLoadedComponent,
    ],
    providers: [PortfolioAgGridColumnMapperService],
    templateUrl: './accounts.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'd-block h-100' },
})
export class PortfolioAccountsComponent implements OnInit {
    private changeDetectorRef = inject(ChangeDetectorRef);
    private numberService = inject(WdxNumberService);
    private dateTimeService = inject(WdxDateTimeService);
    private translationsService = inject(TranslationsService);
    private agGridColumnMapperService = inject(
        PortfolioAgGridColumnMapperService,
    );

    @Input() dataTypeLabel = 'Accounts';
    @Input() dataConnectionError?: FriendlyProblemDetails = undefined;
    @Input() portfolioConfig?: InvestmentDataConfiguration;
    @Input() localeCode?: string | null | undefined;
    @Input() set accountsData(value: AccountsData[] | null | undefined) {
        if (!value) {
            this.status = 'loading';
            return;
        }

        this.status = 'loaded';
        this.rowData = value;
        if (!this.rowData.length) {
            this.status = 'noData';
        }
        this.filteredRowData = [...this.rowData];
        this.changeDetectorRef.markForCheck();
    }
    public status: PortfolioLoadingStates = 'loading';
    public readonly WdxButtonStyle = WdxButtonStyle;
    public selectedGroupby = 0;
    public loading = true;
    public filteredRowData?: AccountsData[];
    public groupDisplayType: RowGroupingDisplayType = 'groupRows';
    private gridApi?: GridApi;
    private rowData?: AccountsData[];

    public gridOptions: GridOptions = {
        domLayout: 'autoHeight',
        pagination: true,
        paginationPageSize: 20,
        defaultColDef: {
            flex: 1,
            sortable: true,
            minWidth: 75,
            contextMenuItems: ['copy'],
        },
        onGridReady: this.onGridReady.bind(this),
    };

    public colDefs?: ColDef[];
    public allColDefs: ColDef[] = [
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_ACCOUNTS_ACCOUNT_NAME',
            ),
            field: 'name',
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_ACCOUNTS_CODE',
            ),
            field: 'uniqueId',
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_ACCOUNTS_IBAN_NUMBER',
            ),
            field: 'bankIban',
        },

        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_ACCOUNTS_CURRENCY',
            ),
            field: 'currency.isoCode',
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_ACCOUNTS_OPENING_DATE',
            ),
            field: 'openingDate',
            minWidth: 150,
            valueGetter: DATE_STRING_VALUE_GETTER,
            valueFormatter: DATE_CELL_VALUE_FORMATTER(
                this.dateTimeService,
                WdxDateFormat.AbsoluteDate,
            ),
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_ACCOUNTS_LATEST_VALUE',
            ),
            headerClass: 'ag-right-aligned-header',
            field: 'balance',
            cellClass: 'text-end',
            minWidth: 115,
            maxWidth: 180,
            comparator: numberFieldPropertyComparator('balance', 'value'),
            valueFormatter: CURRENCY_VALUE_FORMATTER(this.numberService),
        },
    ];

    ngOnInit(): void {
        const lang = this.localeCode?.split('-')[0] || 'en';
        this.gridOptions.localeText =
            AG_GRID_LOCALES[lang] || AG_GRID_LOCALE_EN;

        const colConfig = this.portfolioConfig?.widgets?.find(
            ({ name }) => name === 'PortfolioAccounts',
        );

        const primaryColumnsInConfig = colConfig?.columns?.filter(
            ({ sectionType }) =>
                sectionType === InvestmentDataSectionType.Primary,
        );

        if (primaryColumnsInConfig) {
            // TODO: below is intentionally left commented out, we need to do further work before
            // enabling dynamic column configuration for accounts, with these the groupby does not work
            // we either need to make that configurable as well or remove it
            // this.colDefs = primaryColumnsInConfig.map((col) =>
            //     this.agGridColumnMapperService.createColDef(col),
            // );
            this.colDefs = R.clone(this.allColDefs);
        } else {
            this.colDefs = R.clone(this.allColDefs);
        }
    }

    public onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
    }

    public search(searchTerm: string) {
        if (!searchTerm) {
            this.filteredRowData = [...(this.rowData || [])];
            return;
        }
        const cleanedSearch = searchTerm.trim().toLowerCase();
        this.filteredRowData = this.rowData?.filter(
            (row) =>
                row.name?.toLowerCase().includes(cleanedSearch) ||
                row.bankIban?.toLowerCase().includes(cleanedSearch),
        );
    }
}
